import { Link } from 'react-router-dom';
import { useAuthStore } from '@shared/state';
import { SUBSCRIPTION_TIERS } from '@shared/constants';

interface UploadCounterProps {
  uploadsUsed: number;
  existingContentId?: string | null;
}

export function UploadCounter({ uploadsUsed, existingContentId }: UploadCounterProps) {
  const user = useAuthStore((s) => s.user);
  const tier = (user?.subscription ?? 'free') as keyof typeof SUBSCRIPTION_TIERS;
  const tierConfig = SUBSCRIPTION_TIERS[tier];

  if (tier === 'free') {
    if (uploadsUsed >= 1) {
      return (
        <div className="text-center space-y-3">
          <p className="text-sm text-content-muted font-body">
            You've used your free upload. Upgrade to Learner ($9.99/mo) to upload more content and unlock all 8 CogniPrint dimensions.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/pricing" className="text-sm font-body font-semibold text-indigo-400 hover:text-indigo-300 transition-colors">
              Upgrade
            </Link>
            {existingContentId && (
              <Link to={`/learn/${existingContentId}`} className="text-sm font-body text-content-muted hover:text-content transition-colors">
                Your existing learning path
              </Link>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="text-center">
        <p className="text-sm text-content-muted font-body">
          Free tier: 1 upload, up to 10 pages. <Link to="/pricing" className="text-indigo-400 hover:text-indigo-300 transition-colors">Upgrade for unlimited uploads.</Link>
        </p>
        <p className="text-xs text-content-dim font-body mt-1">
          This is enough to experience your personalised learning path. Upload a chapter, a study guide, or a set of notes.
        </p>
      </div>
    );
  }

  const included = tierConfig.uploadsPerMonth;
  const additionalPrice = tierConfig.additionalUploadPrice;
  const atLimit = uploadsUsed >= included;

  return (
    <div className="text-center">
      <p className={`text-sm font-body ${atLimit ? 'text-warning' : 'text-content-muted'}`}>
        {uploadsUsed} of {included} uploads used this month
      </p>
      {additionalPrice !== null && (
        <p className="text-xs text-content-dim font-body mt-0.5">
          Additional uploads: ${additionalPrice.toFixed(2)} each
        </p>
      )}
    </div>
  );
}
